/**
 * @module utils/interpolate
 * Pointer interpolation between tracking frames.
 *
 * MediaPipe updates at ~25–35 fps while the render loop runs at 60 fps.
 * Rather than snapping the pointer to the latest tracking position each
 * render frame (which looks jerky), we linearly interpolate between the
 * previous and current tracking positions based on the fraction of the
 * expected tracking interval that has elapsed since the last update.
 *
 * This keeps blade movement visually smooth even at half the tracking rate.
 */

import type { PointerPosition } from "../tracking/pointer.js";

/**
 * Tracks one hand's previous and current tracking positions and
 * produces interpolated screen-space positions for each render frame.
 */
export class PointerInterpolator {
  private _prev: PointerPosition | null = null;
  private _curr: PointerPosition | null = null;
  /** Timestamp (ms) when _curr was recorded. */
  private _currTime: number | null = null;
  /** Timestamp (ms) when _prev was recorded. */
  private _prevTime: number | null = null;

  /**
   * Called from the tracking loop whenever a new position arrives.
   *
   * @param pos - New smoothed fingertip position (screen pixels).
   * @param timestamp - Tracking frame timestamp (ms).
   */
  push(pos: PointerPosition, timestamp: number): void {
    this._prev = this._curr;
    this._prevTime = this._currTime;
    this._curr = pos;
    this._currTime = timestamp;
  }

  /**
   * Returns the interpolated pointer position for the current render frame,
   * or `null` if no tracking data is available yet.
   *
   * @param nowMs - Current render frame timestamp (ms).
   * @param expectedIntervalMs - Expected tracking interval (ms); controls
   *   how far ahead we extrapolate beyond the latest sample.
   */
  get(nowMs: number, _expectedIntervalMs: number): PointerPosition | null {
    if (this._curr === null || this._currTime === null) return null;

    // If we only have one sample, just return it (no interpolation possible).
    if (this._prev === null || this._prevTime === null) return this._curr;

    // Alpha: how far between prev and curr are we?
    // Clamp to [0, 1.5] — allow slight extrapolation but cap runaway drift.
    const elapsed = nowMs - this._prevTime;
    const span = this._currTime - this._prevTime;
    const alpha = span > 0 ? Math.min(elapsed / span, 1.5) : 1;

    return {
      x: this._prev.x + (this._curr.x - this._prev.x) * alpha,
      y: this._prev.y + (this._curr.y - this._prev.y) * alpha,
    };
  }

  /**
   * Like {@link get}, but keeps the pointer ALIVE through tracking dropouts:
   * past the newest sample it extrapolates along the last velocity with an
   * exponential decay (time-constant `tauMs`), so the cursor glides to a
   * stop instead of freezing or vanishing when the camera loses the hand
   * (fast hands motion-blur the frame and detection drops for a few frames).
   *
   * Returns `null` once the dropout exceeds `maxCoastMs`.
   */
  getCoasted(
    nowMs: number,
    opts: { maxCoastMs: number; tauMs: number },
  ): PointerPosition | null {
    if (this._curr === null || this._currTime === null) return null;

    const elapsed = nowMs - this._currTime;
    if (elapsed > opts.maxCoastMs) return null;

    // Single sample: nothing to derive velocity from — hold position.
    if (this._prev === null || this._prevTime === null) return this._curr;

    const span = this._currTime - this._prevTime;
    // Still between samples (or degenerate span): normal interpolation.
    if (elapsed <= 0 || span <= 0) return this.get(nowMs, span);

    // Decaying-velocity glide. Displacement integrates v·e^(-t/τ), giving
    // τ·(1 − e^(-t/τ)) — linear continuation at t→0 (seamless handoff from
    // get()'s extrapolation), asymptotic stop at t≫τ.
    const vx = (this._curr.x - this._prev.x) / span;
    const vy = (this._curr.y - this._prev.y) / span;
    const glide = opts.tauMs * (1 - Math.exp(-elapsed / opts.tauMs));

    return {
      x: this._curr.x + vx * glide,
      y: this._curr.y + vy * glide,
    };
  }

  /**
   * Returns the raw (non-interpolated) latest tracking position,
   * or `null` if no data yet.
   */
  get latest(): PointerPosition | null {
    return this._curr;
  }

  /** Clears all tracking history (call when the hand disappears). */
  reset(): void {
    this._prev = null;
    this._curr = null;
    this._currTime = null;
    this._prevTime = null;
  }
}
